import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faChevronDoubleDown,
  faChevronDoubleUp,
  faGift,
  faTrophy,
} from '@fortawesome/pro-solid-svg-icons';
import {
  FormattedMessage,
  MessageDescriptor,
  defineMessages,
} from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon, IconDefinition } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { Color } from '@sorare/core/src/style/types';

import { GameweekDate } from 'components/so5/LastFixtureStory/GameweekDate';

import { RewardStepType } from '../type';
import { Title_so5Fixture } from './__generated__/index.graphql';

export const TitleRow = styled(Horizontal)`
  padding: 0 var(--double-unit);
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: 80px;
  }
`;
export const TitleWrapper = styled.div`
  font-size: var(--fs-24);
  text-align: center;
  @media ${tabletAndAbove} {
    font-size: var(--fs-56);
  }
`;

const titles: Record<RewardStepType, MessageDescriptor> = defineMessages({
  [DivisionChange.PROMOTED]: {
    defaultMessage: 'You got promoted',
    id: 'LastFixtureStoryStep.promotion',
  },
  [DivisionChange.RELEGATED]: {
    defaultMessage: 'You can do better!',
    id: 'LastFixtureStoryStep.relegation',
  },
  leveledUp: {
    defaultMessage: 'Leveled up!',
    id: 'LastFixtureStoryStep.leveledUp',
  },
  recap: {
    defaultMessage: 'Rewards Recap',
    id: 'LastFixtureStoryStep.recap',
  },
  rewards: {
    defaultMessage: 'Reward time',
    id: 'LastFixtureStoryStep.rewards',
  },
});
const colors: Record<RewardStepType, Color> = {
  [DivisionChange.PROMOTED]: 'var(--c-score-high)',
  [DivisionChange.RELEGATED]: 'var(--c-static-red-600)',
  leveledUp: 'var(--c-score-high)',
  recap: 'var(--c-nd-600)',
  rewards: 'var(--c-nd-600)',
};
const icons: Record<RewardStepType, IconDefinition> = {
  [DivisionChange.PROMOTED]: faChevronDoubleUp,
  [DivisionChange.RELEGATED]: faChevronDoubleDown,
  leveledUp: faChevronDoubleUp,
  recap: faGift,
  rewards: faTrophy,
};

type Props = {
  type: RewardStepType;
  so5Fixture?: Title_so5Fixture;
};
export const Title = ({ type, so5Fixture }: Props) => {
  const isRelegation = type === DivisionChange.RELEGATED;
  return (
    <Vertical center>
      {so5Fixture && (
        <GameweekDate
          startDate={so5Fixture.startDate}
          endDate={so5Fixture.endDate}
          colors={{
            bg: isRelegation
              ? 'linear-gradient(90deg, #f23b27 0%, #9d190a 100%)'
              : 'linear-gradient(90deg, #22ff60 0%, #14993a 100%)',
            fg: 'var(--c-black)',
          }}
        />
      )}
      <TitleRow>
        <FontAwesomeIcon icon={icons[type]} color={colors[type]} size="xl" />
        <TitleWrapper>
          <FormattedMessage {...titles[type]} />
        </TitleWrapper>
        <FontAwesomeIcon icon={icons[type]} size="xl" color={colors[type]} />
      </TitleRow>
    </Vertical>
  );
};

Title.fragments = {
  so5Fixture: gql`
    fragment Title_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
    }
  ` as TypedDocumentNode<Title_so5Fixture>,
};
